/**
 * Shared theme styles: status badge, status dot, and keyframe animations.
 * These are used by define, render, and ref features.
 */

import { EditorView } from "@codemirror/view";
import { CM_WIDGET_FONT_FAMILY } from "./theme-helpers";

/* ── Shared status widget styles ── */
export const sharedTheme = EditorView.baseTheme({
    ".cm-magic-pretty-content": {
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        minWidth: "0",
    },
    ".cm-magic-pretty-primary": {
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        minWidth: "0",
        flexShrink: "0",
    },
    ".cm-magic-watch-indicator": {
        alignItems: "center",
        color: "var(--color-info)",
        display: "inline-flex",
        height: "14px",
        justifyContent: "center",
        marginLeft: "1px",
        opacity: "0.86",
        width: "14px",
    },
    ".cm-magic-watch-indicator svg": {
        display: "block",
        height: "12px",
        width: "12px",
    },
    ".cm-magic-end-pretty": {
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        fontFamily: CM_WIDGET_FONT_FAMILY,
        fontSize: "12px",
        lineHeight: "1",
        verticalAlign: "baseline",
        opacity: "0.68",
        cursor: "pointer",
    },
    ".cm-magic-end-pretty:hover": {
        opacity: "0.9",
    },
    ".cm-magic-end-pretty-icon": {
        backgroundColor: "var(--cm-magic-end-accent)",
        color: "white",
    },
    ".cm-magic-end-pretty-label": {
        color: "var(--cm-magic-end-accent)",
        fontWeight: "500",
        fontSize: "11px",
        letterSpacing: "0.01em",
    },
    ".cm-magic-end-pretty-define": {
        "--cm-magic-end-accent": "var(--cm-define-accent)",
    },
    ".cm-magic-end-pretty-meta": {
        "--cm-magic-end-accent": "var(--cm-meta-accent)",
    },
    ".cm-magic-end-pretty-use": {
        "--cm-magic-end-accent": "var(--cm-use-accent)",
    },
    ".cm-magic-end-pretty-content": {
        "--cm-magic-end-accent": "var(--cm-content-accent)",
    },
    ".cm-magic-end-pretty-for": {
        "--cm-magic-end-accent": "var(--cm-for-accent)",
    },
    ".cm-magic-end-pretty-import": {
        "--cm-magic-end-accent": "var(--cm-import-accent)",
    },

    /* L0/L1 shared detail slots for pretty widgets */
    ".cm-magic-hover-detail": {
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        minWidth: "0",
        maxWidth: "0px",
        overflow: "hidden",
        opacity: "0",
        transform: "translateX(-3px)",
        pointerEvents: "none",
        flexShrink: "1",
        whiteSpace: "nowrap",
        transition: "max-width 0.18s ease, opacity 0.14s ease, transform 0.14s ease",
    },
    ".cm-line:hover .cm-magic-hover-detail": {
        maxWidth: "0px",
        opacity: "0",
        transform: "translateX(-3px)",
    },
    ".cm-editor.cm-magic-hide-hover-detail .cm-magic-hover-detail": {
        maxWidth: "0px !important",
        opacity: "0 !important",
        transform: "translateX(-3px) !important",
    },
    ".cm-editor.cm-magic-hide-hover-detail .cm-line:hover .cm-magic-hover-detail": {
        maxWidth: "0px !important",
        opacity: "0 !important",
        transform: "translateX(-3px) !important",
    },
    ".cm-magic-hover-sep": {
        color: "var(--cm-magic-hover-muted, currentColor)",
        opacity: "0.55",
        fontSize: "11px",
        flexShrink: "0",
    },
    ".cm-magic-hover-summary": {
        color: "var(--cm-magic-hover-color, currentColor)",
        opacity: "0.88",
        fontSize: "11px",
        lineHeight: "1.2",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxWidth: "220px",
        minWidth: "0",
        /* letterSpacing removed — caused visual spacing artifacts with $ref widgets */
    },
    ".cm-magic-hover-kind": {
        color: "var(--cm-magic-hover-kind-color, var(--cm-magic-hover-color, currentColor))",
        opacity: "0.78",
        fontSize: "10px",
        lineHeight: "1.2",
        whiteSpace: "nowrap",
        flexShrink: "0",
        padding: "1px 5px",
        borderRadius: "9999px",
        backgroundColor:
            "var(--cm-magic-hover-kind-bg, color-mix(in srgb, var(--color-muted-foreground) 12%, transparent))",
        border:
            "1px solid var(--cm-magic-hover-kind-border, color-mix(in srgb, var(--color-muted-foreground) 18%, transparent))",
    },

    /* ── Syntax letter badge — chemistry-formula flex layout ── */
    /*
     * Structure:  [Main] [Aside]
     *                     ┌ sup ┐
     *                     └ sub ┘
     * Aside is a flex-column that stacks sup/sub vertically.
     * When only one marker exists it still aligns naturally.
     */
    ".cm-magic-syntax-badge": {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: "16px",
        height: "16px",
        borderRadius: "3px",
        boxSizing: "border-box",
        fontFamily: CM_WIDGET_FONT_FAMILY,
        lineHeight: "1",
        flexShrink: "0",
        verticalAlign: "baseline",
        paddingLeft: "3px",
        paddingRight: "2px",
    },
    ".cm-magic-syntax-badge.cm-magic-syntax-badge-template": {
        color: "var(--cm-magic-syntax-badge-accent, currentColor)",
        backgroundColor:
            "color-mix(in oklab, var(--cm-magic-syntax-badge-accent, currentColor) 7%, transparent)",
        border:
            "1px dashed color-mix(in oklab, var(--cm-magic-syntax-badge-accent, currentColor) 56%, transparent)",
    },
    /* Main letter — bold, centred vertically by parent flex */
    ".cm-magic-syntax-badge-main": {
        fontSize: "11px",
        fontWeight: "700",
        letterSpacing: "0.02em",
        lineHeight: "1",
    },
    /* Right aside column — stacks sup (top) and sub (bottom) */
    ".cm-magic-syntax-badge-aside": {
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "center",
        marginLeft: "1px",
        gap: "0px",
        lineHeight: "1",
    },
    /* Superscript — top of aside column */
    ".cm-magic-syntax-badge-sup": {
        fontSize: "7px",
        fontWeight: "700",
        lineHeight: "1",
        opacity: "0.9",
        marginTop: "-1px",
    },
    /* Subscript — bottom of aside column */
    ".cm-magic-syntax-badge-sub": {
        fontSize: "7px",
        fontWeight: "700",
        lineHeight: "1",
        opacity: "0.85",
        marginBottom: "-1px",
    },

    /* Status badge widget — hidden by default, shown on hover (L1) */
    ".cm-magic-badge": {
        display: "none",
        alignItems: "center",
        marginLeft: "6px",
        fontSize: "11px",
        fontWeight: "500",
        fontFamily: CM_WIDGET_FONT_FAMILY,
        verticalAlign: "middle",
        userSelect: "none",
        lineHeight: "1",
        padding: "1px 6px",
        borderRadius: "9999px",
        border: "1px solid",
    },
    /* L1: show full badge on line hover */
    ".cm-line:hover .cm-magic-badge": {
        display: "inline-flex",
    },
    /* Inline widgets own their status badges internally. CodeMirror inserts
     * widget buffer nodes around replacements, so sibling selectors are not
     * reliable here. */
    ".cm-line:hover .cm-magic-inline-status-host .cm-magic-badge": {
        display: "none",
    },
    ".cm-line:hover .cm-magic-inline-status-host:hover .cm-magic-badge": {
        display: "inline-flex",
    },
    /* Suppress L1 badge when the pointer is over the action buttons.
     * CSS :hover updates immediately when pointer-events transitions
     * from none→auto, unlike JS mouseover which requires mouse movement. */
    ".cm-line:hover:has(.cm-magic-btn-group:hover) .cm-magic-badge": {
        display: "none !important",
    },
    ".cm-line:hover:has(.cm-magic-btn-group:hover) .cm-magic-status-dot": {
        display: "inline-block !important",
    },
    ".cm-magic-badge-idle": {
        display: "none",
    },
    ".cm-magic-badge-cached": {
        display: "none",
    },
    /* Status dot — compact indicator for L0 */
    ".cm-magic-status-dot": {
        display: "inline-block",
        width: "6px",
        height: "6px",
        borderRadius: "50%",
        marginLeft: "6px",
        verticalAlign: "middle",
    },
    ".cm-magic-status-dot-idle": {
        display: "none",
    },
    ".cm-magic-status-dot-cached": {
        display: "none",
    },
    ".cm-magic-status-dot-running": {
        backgroundColor: "var(--status-running)",
        animation: "cm-magic-pulse 1.4s ease-in-out infinite",
    },
    ".cm-magic-status-dot-success": {
        display: "none",
    },
    ".cm-magic-status-dot-error": {
        backgroundColor: "var(--status-error)",
    },
    ".cm-magic-badge-running": {
        color: "var(--status-running)",
        backgroundColor:
            "color-mix(in oklab, var(--status-running) 12%, transparent)",
        borderColor:
            "color-mix(in oklab, var(--status-running) 30%, transparent)",
    },
    ".cm-magic-badge-dot": {
        display: "inline-block",
        width: "6px",
        height: "6px",
        borderRadius: "50%",
        backgroundColor: "currentColor",
        verticalAlign: "middle",
        animation: "cm-magic-pulse 1.4s ease-in-out infinite",
    },
    ".cm-magic-badge-success": {
        color: "var(--status-success)",
        backgroundColor:
            "color-mix(in oklab, var(--status-success) 12%, transparent)",
        borderColor:
            "color-mix(in oklab, var(--status-success) 28%, transparent)",
    },
    ".cm-magic-badge-error": {
        color: "var(--status-error)",
        backgroundColor:
            "color-mix(in oklab, var(--status-error) 12%, transparent)",
        borderColor:
            "color-mix(in oklab, var(--status-error) 28%, transparent)",
    },
});

/* ── Keyframe animations for running status ── */
export const spinKeyframes = EditorView.baseTheme({
    "@keyframes cm-magic-pulse": {
        "0%": { opacity: "1", transform: "scale(1)" },
        "50%": { opacity: "0.2", transform: "scale(0.6)" },
        "100%": { opacity: "1", transform: "scale(1)" },
    },
    "@keyframes cm-magic-border-breathe": {
        from: {
            borderLeftColor:
                "color-mix(in oklab, var(--status-running) 25%, transparent)",
            backgroundColor:
                "color-mix(in oklab, var(--status-running) 2%, transparent)",
        },
        to: {
            borderLeftColor:
                "color-mix(in oklab, var(--status-running) 80%, transparent)",
            backgroundColor:
                "color-mix(in oklab, var(--status-running) 7%, transparent)",
        },
    },
    "@keyframes cm-magic-ref-breathe": {
        from: {
            backgroundColor:
                "color-mix(in oklab, var(--cm-ref-pending) 6%, transparent)",
            opacity: "0.7",
        },
        to: {
            backgroundColor:
                "color-mix(in oklab, var(--cm-ref-pending) 15%, transparent)",
            opacity: "1",
        },
    },
    "@keyframes cm-magic-ref-run-breathe": {
        from: {
            backgroundColor:
                "color-mix(in oklab, var(--cm-ref-running) 4%, transparent)",
            opacity: "0.7",
        },
        to: {
            backgroundColor:
                "color-mix(in oklab, var(--cm-ref-running) 16%, transparent)",
            opacity: "1",
        },
    },
});
